// Cookie consent storage helpers. No React dependencies — testable in isolation.
//
// Consent is stored in TWO places so it survives partial clears:
//   1. localStorage  — primary store (fast synchronous read)
//   2. a first-party cookie of the same name — survives localStorage.clear()
//      (e.g. on logout) and works where localStorage writes fail (some
//      private-browsing modes). getStoredConsent() self-heals localStorage
//      from the cookie when only the cookie copy exists.
//
// Consent expires after CONSENT_MAX_AGE_DAYS, after which the banner
// legitimately re-prompts.
//
// Bumping CONSENT_VERSION forces every user to re-consent on next visit.
// Do that when the categories change or you materially update the Cookie Policy.

export const CONSENT_VERSION = 1;
export const STORAGE_KEY = "talentfora_cookie_consent";
export const CONSENT_MAX_AGE_DAYS = 365;

const CONSENT_MAX_AGE_MS = CONSENT_MAX_AGE_DAYS * 24 * 60 * 60 * 1000;
const COOKIE_MAX_AGE_SECONDS = CONSENT_MAX_AGE_DAYS * 24 * 60 * 60;

export const DEFAULT_PREFS = Object.freeze({
  necessary: true,
  functional: false,
  analytics: false,
  marketing: false,
});

const isBrowser = () => typeof window !== "undefined" && typeof window.localStorage !== "undefined";

// ── Cookie mirror helpers ────────────────────────────────────────────────────

function readConsentCookie() {
  if (typeof document === "undefined") return null;
  try {
    const entry = document.cookie.split(";").find((c) => c.trim().startsWith(`${STORAGE_KEY}=`));
    if (!entry) return null;
    const raw = decodeURIComponent(entry.split("=").slice(1).join("=").trim());
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

function writeConsentCookie(payload) {
  if (typeof document === "undefined") return;
  try {
    // Host-only cookie (no Domain attribute). Secure only on https so the
    // cookie still works on http://localhost in development.
    const secure = typeof window !== "undefined" && window.location.protocol === "https:" ? "; Secure" : "";
    document.cookie = `${STORAGE_KEY}=${encodeURIComponent(JSON.stringify(payload))}; path=/; max-age=${COOKIE_MAX_AGE_SECONDS}; SameSite=Lax${secure}`;
  } catch {
    // ignore — localStorage copy still covers this browser
  }
}

function clearConsentCookie() {
  if (typeof document === "undefined") return;
  try {
    document.cookie = `${STORAGE_KEY}=; path=/; max-age=0; SameSite=Lax`;
  } catch {
    // ignore
  }
}

// ── Validation ───────────────────────────────────────────────────────────────

function normalizeConsent(parsed) {
  if (!parsed || typeof parsed !== "object") return null;
  if (parsed.version !== CONSENT_VERSION) return null;
  // Consent without a parseable timestamp, or older than the max age, is
  // treated as expired so the banner re-prompts.
  const ts = parsed.timestamp ? Date.parse(parsed.timestamp) : NaN;
  if (Number.isNaN(ts) || Date.now() - ts > CONSENT_MAX_AGE_MS) return null;
  return {
    version: CONSENT_VERSION,
    necessary: true,
    functional: !!parsed.functional,
    analytics: !!parsed.analytics,
    marketing: !!parsed.marketing,
    timestamp: parsed.timestamp,
  };
}

// ── Public API ───────────────────────────────────────────────────────────────

export function getStoredConsent() {
  if (!isBrowser()) return null;

  // Primary: localStorage
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const consent = normalizeConsent(JSON.parse(raw));
      if (consent) return consent;
    }
  } catch {
    // fall through to the cookie mirror
  }

  // Fallback: cookie mirror (survives localStorage.clear on logout)
  const fromCookie = normalizeConsent(readConsentCookie());
  if (fromCookie) {
    // Self-heal the localStorage copy
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(fromCookie));
    } catch {
      // ignore — cookie copy keeps working
    }
    return fromCookie;
  }

  return null;
}

export function setStoredConsent(prefs) {
  if (!isBrowser()) return null;
  const next = {
    version: CONSENT_VERSION,
    necessary: true,
    functional: !!prefs?.functional,
    analytics: !!prefs?.analytics,
    marketing: !!prefs?.marketing,
    timestamp: new Date().toISOString(),
  };
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  } catch {
    // localStorage can fail (full, disabled, private mode); the cookie below still persists
  }
  writeConsentCookie(next);
  return next;
}

export function clearConsent() {
  if (!isBrowser()) return;
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    // ignore
  }
  clearConsentCookie();
}

export const PREFERENCES_EVENT = "open-cookie-preferences";

export function openPreferences() {
  if (!isBrowser()) return;
  window.dispatchEvent(new Event(PREFERENCES_EVENT));
}
